<template>
  <div v-if="currentStepIsValid">
    <v-select
      v-if="hasSelectableOptions && hasSteps"
      :ref="selectInputRef"
      :items="selectableOptions"
      item-text="title"
      item-value="value"
      :value="selectedValue"
      :menu-props="{ maxHeight: maxHeight }"
      outlined
      class="requiredHubspotFormInput"
      @change="setNextStep"
    >
      <template #label>
        <label :class="{ [$style.requiredOption]: !selectedValue }">{{
          $t('hubspot.wizard.steps.placeholder')
        }}</label>
      </template>
    </v-select>
  </div>
</template>

<script>
import { stepConfig, stepConfigUA } from '@/js/components/HubSpot/steps.js'
import forms from '@/js/components/HubSpot/Forms/hubspot-forms'
import HubSpotUaFormMixin from '@/js/mixins/hubspot-ua-form-mixin.js'

export default {
  name: 'HubSpotStep',
  mixins: [HubSpotUaFormMixin],
  props: {
    steps: {
      type: Array,
      default: () => [],
    },
    currentStepId: {
      type: String,
      default: '',
    },
    brand: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedValue: undefined,
    }
  },
  computed: {
    config() {
      return this.isUAForm ? stepConfigUA : stepConfig
    },
    targetSelect() {
      return this.$refs.select
    },
    maxHeight() {
      return 560
    },
    routerQuery() {
      return this.$route.query
    },
    queryFormName() {
      return this.routerQuery?.formName
    },
    previousStep() {
      // returns previous step config
      const currentIndex = this.steps.indexOf(this.currentStepId)
      const previousStepIdentifier = this.steps[currentIndex - 1]
      return this.findStepInConfig(previousStepIdentifier)
    },
    includedInPreviousStepOptions() {
      // check if previous step present
      if (this.previousStep) {
        // return bool if current step is included in previous step options
        return !!this.previousStep?.options?.find(option => option.value === this.currentStepId)
      }

      return false
    },
    currentStep() {
      // get current step from export base config
      return this.findStepInConfig(this.currentStepId)
    },
    currentStepIsValid() {
      // check if previousStep present & currentStep is included in options from previousStep & / else currentStep is present (e.g. it's the first step)
      return this.previousStep
        ? !!this.currentStep && this.includedInPreviousStepOptions
        : !!this.currentStep
    },
    nextStepId() {
      const currentIndex = this.steps.indexOf(this.currentStepId)
      return this.steps[currentIndex + 1]
    },
    nextValidStepValue() {
      // checks for next step in query and if it is a valid next step for current step then returns it's id or undefined
      const nextStepIsValid = this.findStepInConfig(this.nextStepId)
      let nextValidStepValue = undefined
      if (this.nextStepId && this.currentStep?.options?.length && nextStepIsValid) {
        // check for id in options from currentStep return value for use in selectedValue (set by watcher)
        nextValidStepValue = this.currentStep?.options?.find(
          option => option.value === this.nextStepId,
        )?.value
      }

      return nextValidStepValue
    },
    validFormNameOptionValue() {
      // checks if query.formName is included in current step options
      let nextOptionValue = undefined
      if (this.queryFormName) {
        // check for formName in options from currentStep return value for use in selectedValue (set by watcher)
        nextOptionValue = this.currentStep?.options?.find(
          option => option.formName === this.queryFormName,
        )?.value
      }
      return nextOptionValue
    },
    validNextStepOrFormNameValue() {
      // returns the value of the valid next step or formName for selectedValue
      return this.nextValidStepValue ?? this.validFormNameOptionValue
    },
    selectableOptions() {
      // transforms options to v-select compatible array
      const options = []
      this.currentStep.options?.forEach(option => {
        // only use step with definition in config => '@/js/components/HubSpot/steps.js or formName has to be present in option
        if (this.findStepInConfig(option.value) || option.formName) {
          options.push({
            title: this.$t(`hubspot.wizard.steps.${option.translationKey}`),
            value: option.value,
          })
        } else {
          console.warn(
            `Could not find definition for ${option.value}. Please define it in @/js/components/HubSpot/steps.js`,
          )
        }
      })
      return options
    },
    isCurrentLastStep() {
      // returns if current step is currently the last one in step query
      return this.steps.indexOf(this.currentStepId) === this.steps.length - 1
    },
    isFinalStep() {
      // returns if current step is the last one in query AND there is a form name, thus current is the final step
      return this.isCurrentLastStep && this.hasFormNameInQuery
    },
    hasFormNameInQuery() {
      return !!this.queryFormName
    },
    hasSelectableOptions() {
      return !!this.selectableOptions?.length
    },
    hasSteps() {
      return !!this.$route.query?.steps?.length
    },
    selectInputRef() {
      // used to identify select input
      return `select-${this.currentStepId}`
    },
    currentStepOptionByValue() {
      const currentStepOptionByValue = this.currentStep?.options?.find(
        option => option.value === this.validFormNameOptionValue,
      )
      return currentStepOptionByValue
    },
    hideSubmitButton() {
      // used to emit to HubSpotFormRenderer state of hideSubmitButton
      // returns if the our current step searched by 'validFormNameOptionValue' has a hideSubmitButton state
      if (this.isFinalStep) {
        return this.currentStepOptionByValue?.hideSubmitButton ?? false
      }
      return false
    },
    showAdditionalFields() {
      // used to emit to HubSpotFormRenderer state of showAdditionalFields
      // returns if the our current step searched by 'validFormNameOptionValue' has a showAdditionalFields state
      if (this.isFinalStep) {
        return this.currentStepOptionByValue?.showAdditionalFields ?? false
      }
      return false
    },
    jumpToPageKeys() {
      // used to emit to HubSpotFormRenderer jumpPageKeys
      // returns if the our current step searched by 'validFormNameOptionValue' has jumpPageKeys
      if (this.isFinalStep) {
        return this.currentStepOptionByValue?.jumpToPageKeys ?? false
      }
      return false
    },
  },
  watch: {
    validNextStepOrFormNameValue: {
      handler(nextProps) {
        // updates selectedValue for v-select depending on valid next step/form value
        this.selectedValue = nextProps
      },
      immediate: true,
    },
    routerQuery: {
      handler() {
        if (this.$route?.name !== 'default') {
          return
        }

        this.$emit('hideSubmitButton', this.hideSubmitButton)
        this.$emit('showAdditionalFields', this.showAdditionalFields)
        this.$emit('jumpToPageKeys', this.jumpToPageKeys)
        this.$emit('resetOutcomeMessage')
        let query = undefined
        const formName = undefined

        if (!this.currentStepIsValid) {
          // 1.case = current is not a valid step => clear steps & formName (including self)
          const steps = this.getClearedCurrentQuery()
          query = { ...this.$route.query, steps, formName }
        } else if (!this.isCurrentLastStep && !this.nextValidStepValue) {
          // 2.case = next step is not a valid step => clear everything AFTER current step
          const steps = this.getClearedCurrentQuery(1)
          query = { ...this.$route.query, steps, formName }
        } else if (
          this.isFinalStep &&
          (!this.validFormNameOptionValue || !(this.queryFormName in forms))
        ) {
          // 3.case = final step, but formName in query is not a valid option for current step => clear formName
          query = { ...this.$route.query, formName }
          this.resetSelectInput()
        }

        if (query) {
          this.pushQuery(query)
        }
      },
      immediate: true,
    },
  },
  methods: {
    setNextStep(nextStepId) {
      // check for next step id in options of current step
      const nextOption = this.findOptionById(nextStepId)
      // this clears every query.step parameter after currentStep to keep history consistent when updating previous (grandparent) select
      const clearedQuery = this.getClearedCurrentQuery(1)
      let steps = ''
      let formName = undefined
      // if nextOption defines a formName render form instead of next step
      if (nextOption.formName) {
        formName = nextOption.formName
        steps = `${clearedQuery}`
      } else {
        // else add nextStep to query
        steps = `${clearedQuery},${nextStepId}`
      }
      this.$emit('scroll-to-anchor-emit')

      this.pushQuery({ ...this.$route.query, steps, formName })
    },
    findStepInConfig(stepId) {
      return this.config.find(step => step.id === stepId)
    },
    findOptionById(optionId) {
      return this.currentStep?.options?.find(option => option.value === optionId)
    },
    getClearedCurrentQuery(startFrom = 0) {
      // clear every step in router query, either including self, or everything after startFrom index
      const clearedSteps =
        this.steps.slice(0, this.steps.indexOf(this.currentStepId) + startFrom) ?? []
      // returns new cleared step query
      return clearedSteps?.join(',')
    },
    resetSelectInput() {
      // Reset internal state, reset v-select manually
      this.selectedValue = undefined
      this.$refs[this.selectInputRef]?.reset()?.blur()
      // I know this looks weird but it works ¯\_(ツ)_/¯
      document.activeElement.blur()
      // reset focus of select input
      this.$nextTick(() => this.$refs[this.selectInputRef]?.blur())
    },
    pushQuery(query) {
      // pushes new query to router -> updates stepper/form
      this.$router.push({
        query,
      })
    },
  },
}
</script>
<style lang="scss" module>
.requiredOption {
  color: var(--error);
}
</style>
